import jsPDF from 'jspdf';
import { TestResults } from '../../../../types';
import { PDF_COLORS } from '../../config/colors';
import { PDF_FONTS, PDF_LAYOUT } from '../../config/fonts';
import { PageManager } from '../../helpers/pageBreaks';
import { getImplementationPhases } from './utils/ImplementationUtilities';

export class ImplementationRoadmapComponent {
  private pdf: jsPDF;
  private pageManager: PageManager;

  constructor(pdf: jsPDF, pageManager: PageManager) {
    this.pdf = pdf;
    this.pageManager = pageManager;
  }

  render(results: TestResults): void {
    this.renderSectionHeader();
    this.renderRoadmapPhases(results);
    this.renderTimelineOverview();
  }

  private renderSectionHeader(): void {
    this.pageManager.checkForPageBreak(100, {
      minSpaceAfter: 50,
      elementType: 'implementation-roadmap',
    });

    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Implementation Roadmap',
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );

    this.pageManager.addElement(PDF_FONTS.size.subheading, {
      spacingAfter: PDF_LAYOUT.spacing.paragraph,
      elementType: 'roadmap-header',
    });
  }

  private renderRoadmapPhases(results: TestResults): void {
    const phases = getImplementationPhases(results);

    phases.forEach((phase, index) => {
      this.renderPhase(phase, index + 1);
    });
  }

  private renderPhase(phase: any, phaseNumber: number): void {
    const phaseHeight = 60;
    this.pageManager.checkForPageBreak(phaseHeight, {
      minSpaceAfter: 30,
      elementType: `phase-${phaseNumber}`,
    });

    // Phase header
    this.pdf.setFillColor(...PDF_COLORS.background.primary);
    this.pdf.setDrawColor(...PDF_COLORS.primary);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY(),
      this.pageManager.getContentWidth(),
      20,
      'FD'
    );

    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      phase.title,
      PDF_LAYOUT.margins.left + 5,
      this.pageManager.getCurrentY() + 12
    );

    this.pageManager.addElement(25, {
      spacingAfter: PDF_LAYOUT.spacing.line,
      elementType: 'phase-header',
    });

    // Phase description
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);

    const description = this.pdf.splitTextToSize(
      phase.description,
      this.pageManager.getContentWidth() - 10
    );

    description.forEach((line: string) => {
      this.pdf.text(line, PDF_LAYOUT.margins.left + 5, this.pageManager.getCurrentY());
      this.pageManager.addLine();
    });

    this.pageManager.addLine();

    // Phase actions
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.small);
    this.pdf.setTextColor(...PDF_COLORS.text.primary);
    this.pdf.text(
      'Key Actions:',
      PDF_LAYOUT.margins.left + 5,
      this.pageManager.getCurrentY()
    );

    this.pageManager.addLine();

    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);

    phase.actions.forEach((action: string) => {
      const actionLines = this.pdf.splitTextToSize(
        `• ${action}`,
        this.pageManager.getContentWidth() - 15
      );

      actionLines.forEach((line: string) => {
        this.pdf.text(line, PDF_LAYOUT.margins.left + 10, this.pageManager.getCurrentY());
        this.pageManager.addLine();
      });
    });

    this.pageManager.addSection();
  }

  private renderTimelineOverview(): void {
    this.pageManager.checkForPageBreak(40, {
      minSpaceAfter: 20,
      elementType: 'timeline-overview',
    });

    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Success Metrics & Timeline',
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );

    this.pageManager.addParagraph();

    const metrics = [
      'Increase overall readiness score by 15-20% within 6 months',
      'Achieve "Ready" status in 2-3 weak categories',
      'Complete export business plan and secure initial funding',
      'Establish key partnerships and operational processes',
      'Launch pilot export operations within 12 months'
    ];

    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);

    metrics.forEach((metric) => {
      const metricLines = this.pdf.splitTextToSize(
        `• ${metric}`,
        this.pageManager.getContentWidth() - 10
      );

      metricLines.forEach((line: string) => {
        this.pdf.text(line, PDF_LAYOUT.margins.left + 5, this.pageManager.getCurrentY());
        this.pageManager.addLine();
      });
    });
  }
}